import type { StrategyMode, StrategyRole, StrategyStatus } from "../api/types";
import { modeAllowed, modeLabel } from "./signals";

/**
 * Чистая часть управления живым режимом стратегий.
 *
 * Сеть, таймеры и React живут в компонентах — здесь только правила, которые
 * можно проверить таблицей. Разделение здесь не стилевое, и довод остался
 * прежним по сути: под тесты `vitest.config.ts` берёт только файлы `.ts`,
 * поэтому компонентный тест обходится дорого — разметку в нём приходится
 * собирать `createElement`, без JSX. Один такой в проекте всё же есть
 * (`components/strategyrowactions.test.ts`), и заведён он ровно там, где чистой
 * функцией не обойтись: проверяется ЖИЗНЬ СОСТОЯНИЯ во времени, между нажатием и
 * вердиктом трейдера. Всё, что можно посчитать таблицей, дешевле держать здесь:
 * иначе оно не проверяется никак.
 *
 * # Что здесь НЕ считается
 *
 * Действующий режим. Он есть строгий минимум потолка окружения и двух ключей
 * оператора, и считает его трейдер, отдавая нотой. Посчитай интерфейс минимум
 * сам — на экране появилось бы ВТОРОЕ определение действующего режима, и
 * разошлось бы оно с тем, по которому уходят деньги. Здесь только РАЗБОР того,
 * что сказал трейдер.
 */

/** Стратегии, у которых есть порт постановки. Зеркало bus.Strategy*. */
export const STRATEGIES = ["decided", "family", "mispriced", "consensus"] as const;

export type StrategyID = (typeof STRATEGIES)[number];

export const STRATEGY_ROLES: readonly StrategyRole[] = ["none", "maker", "taker", "both"];

/** Человеческое имя стратегии. */
export function strategyLabel(id: string): string {
  switch (id) {
    case "decided":
      return "Почти решённое";
    case "family":
      return "Копирование семей";
    case "mispriced":
      return "Расхождение";
    case "consensus":
      return "Котировка когорты";
    default:
      return id;
  }
}

/** Человеческое имя роли. */
export function roleLabel(role: StrategyRole): string {
  switch (role) {
    case "none":
      return "не входить";
    case "maker":
      return "мейкер";
    case "taker":
      return "тейкер";
    case "both":
      return "обе";
  }
}

/** Чем роль расплачивается — то, ради чего переключатель и читают. */
export function roleHint(role: StrategyRole): string {
  switch (role) {
    case "none":
      return "ордеров не будет ни при каком режиме";
    case "maker":
      return "заявкой в стакан: комиссии нет, но есть очередь и неблагоприятный отбор";
    case "taker":
      return "переходом спреда: исполняется сразу, платит комиссию";
    case "both":
      return "решает подсистема; при обеих разрешённых берётся тейкер";
  }
}

/** Разобранная нота режима: что действует, какой ролью и почему. */
export interface ParsedMode {
  mode: StrategyMode | null;
  role: StrategyRole | null;
  /** Почему режим именно такой, словами трейдера. Пусто — причины не назвали. */
  why: string;
}

const MODES: readonly StrategyMode[] = ["off", "shadow", "alert", "auto"];

function asMode(raw: string): StrategyMode | null {
  const v = raw.trim().toLowerCase();
  return (MODES as readonly string[]).includes(v) ? (v as StrategyMode) : null;
}

function asRole(raw: string): StrategyRole | null {
  const v = raw.trim().toLowerCase();
  return (STRATEGY_ROLES as readonly string[]).includes(v) ? (v as StrategyRole) : null;
}

/**
 * Разобрать ноту вида «shadow / taker (потолок окружения)».
 *
 * Разбор, а не структурное поле в ответе, потому что нота — это ЕДИНСТВЕННОЕ
 * место, где действующий режим называет тот, кто его и применяет. Разложи мы её
 * на поля в Go — они бы разошлись с той строкой, что видит оператор в /healthz.
 *
 * Неразобранное значение даёт null, а не подставленное умолчание: «режим
 * неизвестен» и «режим выключен» — разные состояния, и второе означало бы, что
 * оператор что-то выставил.
 */
export function parseMode(note: string | undefined): ParsedMode {
  const out: ParsedMode = { mode: null, role: null, why: "" };
  if (!note) return out;

  let head = note.trim();
  const open = head.indexOf("(");
  if (open >= 0) {
    const close = head.lastIndexOf(")");
    out.why = head.slice(open + 1, close > open ? close : undefined).trim();
    head = head.slice(0, open).trim();
  }
  const [rawMode, rawRole] = head.split("/");
  if (rawMode !== undefined) out.mode = asMode(rawMode);
  if (rawRole !== undefined) out.role = asRole(rawRole);
  return out;
}

/**
 * Поднять режим ВЫШЕ потолка окружения нельзя.
 *
 * То же правило и та же функция, что у копирования (lib/signals.modeAllowed):
 * лесенка в Go одна на всех, и вторая её копия здесь разошлась бы молча.
 */
export function strategyModeAllowed(mode: StrategyMode, ceiling: string | undefined): boolean {
  return modeAllowed(mode, parseMode(ceiling).mode);
}

/**
 * Роль ВЫШЕ потолка тоже не выдаётся, но правило другое: у ролей порядка нет.
 *
 * «Мейкер» и «тейкер» несравнимы, и действующая роль есть их ПЕРЕСЕЧЕНИЕ, а не
 * минимум по списку. Зеркало domain.MinStrategyRole; разойдись они — кнопка
 * предлагала бы роль, которую трейдер молча сузит до none.
 */
export function roleAllowed(role: StrategyRole, ceiling: string | undefined): boolean {
  const top = parseMode(ceiling).role;
  // Потолок неизвестен — это НЕ «потолка нет»: трейдер может не отвечать или не
  // иметь этой подсистемы вовсе. Единственная роль, которая в таком состоянии
  // значит ровно то, что написана, — «не входить».
  if (!top) return role === "none";
  if (role === "none") return true;
  if (top === "both") return true;
  return role === top;
}

/**
 * Переход в `auto` — единственный, после которого ордера идут БЕЗ человека у
 * экрана. У него своя подпись-предупреждение: остальные повышения обещают
 * кнопку, это — её отсутствие.
 */
export function modeWarning(mode: StrategyMode): string | null {
  if (mode !== "auto") return null;
  return "трейдер начнёт ставить ордера сам, без вашего участия";
}

/**
 * Одна строка про стратегию для панели: что действует и сколько уже сделано.
 *
 * Собрана здесь, а не в разметке, ровно затем, чтобы её можно было проверить:
 * «ордеров 0 при 400 строках» и «ордеров 0, потому что режим теневой» на экране
 * выглядят одинаково, а означают разное.
 */
export function strategySummary(st: StrategyStatus | undefined): string {
  if (!st) return "трейдер не отвечает";
  const { mode, role, why } = parseMode(st.mode);
  const head = mode ? modeLabel(mode) : "режим неизвестен";
  const parts: string[] = [head];
  if (role && role !== "none") parts.push(roleLabel(role));
  if (why) parts.push(why);
  const placed = st.placed ?? "0";
  const claimed = st.claimed ?? "0";
  return `${parts.join(", ")} · строк ${claimed}, ордеров ${placed}`;
}

/**
 * Что предложить оператору по одной строке журнала.
 *
 * Три состояния, и различать их обязательно. «Взять» — режим `alert` и выше, а
 * окно ещё идёт. «Поздно» — окно кончилось: вход обосновать нечем, и заявку по
 * нему трейдер тут же снял бы свипером, поэтому кнопка обязана быть выключена,
 * а не отвечать 409 после нажатия. «Нет» — режим ниже `alert`: строка теневая,
 * и кнопка по ней обещала бы действие, которого не существует.
 */
export type RowAction = "take" | "late" | "none";

export function rowAction(
  modeNote: string | undefined,
  windowEnd: string,
  now: number,
): RowAction {
  const mode = parseMode(modeNote).mode;
  if (mode !== "alert" && mode !== "auto") return "none";
  const end = Date.parse(windowEnd);
  // Неразобранная дата — «поздно», а не «можно»: fail-closed, как везде, где
  // числа не сошлись. Кнопка, включённая по нечитаемой дате, обещала бы ордер
  // на окне, о котором мы ничего не знаем.
  if (Number.isNaN(end) || end <= now) return "late";
  return "take";
}
